import ReplayIcon from '@mui/icons-material/Replay';
import StarIcon from '@mui/icons-material/Star';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  typographyClasses,
} from '@mui/material';
import Grid from '@mui/material/Grid/Grid';
import { useQuery } from '@tanstack/react-query';
import { find, first, join, map } from 'lodash';
import moment from 'moment';
import React from 'react';

import { getModels } from 'api/platform';
import CopyableText from 'components/atoms/CopyableText';
import { modelTypesByApiModelValue } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { getStainingMethodByStainType, StainingMethod } from 'interfaces/stainType';
import { formatCellSegmentationGeometryLabel, getCellSegmentationGeometries } from 'utils/models';
import {
  modelIdFromModelUrl,
  stripModelToUrlHashData,
  useModelTypeOptions,
} from 'utils/queryHooks/useModelTypeOptions';
import usePrevious from 'utils/usePrevious';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import ModelsTable, { ModelsTableProps } from '.';

export interface ModelTableWithDefaultSelectionProps extends Omit<ModelsTableProps, 'defaultModelId'> {
  stainingMethod?: StainingMethod;
  onDefaultModelUrlChange: (modelUrl: string) => void;
  debug?: boolean;
  hasModelUrlError?: boolean;
  modelUrlErrorMessage?: string;
  onSelectedModelUrlChange: (modelUrl: string) => void;
}

export const ModelTableWithDefaultSelection: React.FC<React.PropsWithChildren<ModelTableWithDefaultSelectionProps>> = ({
  stainingMethod,
  onDefaultModelUrlChange,
  debug = false,
  ...props
}) => {
  const { onSelectedModelUrlChange, hasModelUrlError, modelUrlErrorMessage, modelType, enabled = true } = props;
  const selectedModelUrl = props.selectedModelUrl;
  const previousSelectedModelUrl = usePrevious(selectedModelUrl);
  // Check if the selected model URL is empty and wasn't just erased by user input
  const isInitialEmptySelectedModelUrl = !selectedModelUrl && !previousSelectedModelUrl;
  const { data: modelTypeOptions } = useModelTypeOptions();
  const systemModelType = find(modelTypeOptions, {
    type: props.modelType,
    stainingMethod: stainingMethod || getStainingMethodByStainType(props.stainType),
  });
  const defaultModelUrl = systemModelType?.defaultModelUrl;
  const resolvedSelectedModelUrl =
    defaultModelUrl && isInitialEmptySelectedModelUrl ? defaultModelUrl : selectedModelUrl;
  const normalizedSelectedModelUrl = stripModelToUrlHashData(resolvedSelectedModelUrl);
  const normalizedDefaultModelUrl = stripModelToUrlHashData(defaultModelUrl);
  const previousDefaultModelUrl = usePrevious(defaultModelUrl);
  const didDefaultModelUrlChange =
    Boolean(previousDefaultModelUrl) && normalizedDefaultModelUrl !== stripModelToUrlHashData(previousDefaultModelUrl);
  const isDefaultModelUrlDifferentThanSelected = normalizedDefaultModelUrl !== normalizedSelectedModelUrl;
  const isDefaultModelSelected = Boolean(normalizedDefaultModelUrl) && !isDefaultModelUrlDifferentThanSelected;

  React.useEffect(() => {
    // If the default model URL changes and is different from the selected model URL,
    // or if the selected model URL is empty (i.e. we are not rebuilding) and it was not just erased (i.e. user pressing backspace)
    // we trigger the onDefaultModelUrlChange callback with the default model URL.
    if (
      defaultModelUrl &&
      ((didDefaultModelUrlChange && isDefaultModelUrlDifferentThanSelected) || isInitialEmptySelectedModelUrl)
    ) {
      if (debug) {
        console.debug(
          `ModelTableWithDefaultSelection: Default model URL changed to ${defaultModelUrl}, triggering onDefaultModelUrlChange.`,
          {
            defaultModelUrl,
            selectedModelUrl,
            isDefaultModelUrlDifferentThanSelected,
            isInitialEmptySelectedModelUrl,
            didDefaultModelUrlChange,
            previousDefaultModelUrl,
          }
        );
      }
      onDefaultModelUrlChange(defaultModelUrl);
    }
  }, [defaultModelUrl]);

  const defaultModelId = modelIdFromModelUrl(defaultModelUrl);
  const selectedModelId = modelIdFromModelUrl(resolvedSelectedModelUrl);

  const { data: defaultModelResponse, isLoading: isDefaultModelLoading } = useQuery({
    queryKey: ['models', { modelId: defaultModelId, modelType }],
    queryFn: () => getModels({ modelId: defaultModelId, modelType }),
    enabled: Boolean(defaultModelId && enabled),
    keepPreviousData: true,
  });
  const { data: selectedModelResponse, isLoading: isSelectedModelLoading } = useQuery({
    queryKey: ['models', { modelId: selectedModelId, modelType }],
    queryFn: () => getModels({ modelId: selectedModelId, modelType }),
    enabled: Boolean(selectedModelId && enabled),
    keepPreviousData: true,
  });

  const defaultModelDetails = first(defaultModelResponse?.items);
  const selectedModelDetails = first(selectedModelResponse?.items);
  const selectedModelGeometries = getCellSegmentationGeometries(selectedModelDetails);

  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  return (
    <>
      <Grid item container direction="row" spacing={1} alignItems="center" wrap="nowrap">
        <Grid item xs>
          <FormControl fullWidth required error={hasModelUrlError}>
            <InputLabel htmlFor="model-url-input">Model Url</InputLabel>
            <OutlinedInput
              id="model-url-input"
              value={resolvedSelectedModelUrl || ''}
              label="Model Url"
              onChange={(event) => {
                event.preventDefault();
                onSelectedModelUrlChange?.(event.target.value);
              }}
              placeholder="Choose model from the table or write here the artifact url"
              error={hasModelUrlError}
              endAdornment={
                Boolean(defaultModelUrl) &&
                (!isDefaultModelUrlDifferentThanSelected ? (
                  <InputAdornment position="end">
                    <Chip label="Default Model" icon={<StarIcon />} color="primary" variant="outlined" />
                  </InputAdornment>
                ) : (
                  <Tooltip
                    title={`The default model for the selected stain type and model type is: ${defaultModelUrl}`}
                    placement="top"
                  >
                    <Chip
                      color="primary"
                      icon={<ReplayIcon />}
                      label="Reset to Default Model"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onDefaultModelUrlChange(defaultModelUrl);
                      }}
                    />
                  </Tooltip>
                ))
              }
            />
            <FormHelperText error sx={{ marginLeft: 1 }}>
              {modelUrlErrorMessage || ' '}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      {selectedModelId ? (
        <Grid item>
          {isSelectedModelLoading ? (
            <Typography variant="body2" color="text.secondary">
              <CircularProgress size={12} sx={{ mr: 1 }} />
              Loading selected model metadata...
            </Typography>
          ) : selectedModelGeometries.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {`Selected model geometries: ${join(
                map(selectedModelGeometries, formatCellSegmentationGeometryLabel),
                ', '
              )}`}
            </Typography>
          ) : null}
        </Grid>
      ) : null}
      {defaultModelUrl && (
        <Grid item width="100%">
          <Card
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSelectedModelUrlChange?.(defaultModelUrl);
            }}
            sx={{ backgroundColor: 'background.paper', cursor: 'pointer' }}
          >
            <CardHeader
              title={
                <FormControl fullWidth>
                  <RadioGroup
                    value={normalizedSelectedModelUrl || ''}
                    onChange={(event) => {
                      event.preventDefault();
                      onSelectedModelUrlChange?.(defaultModelUrl);
                    }}
                    onClick={(event) => event.stopPropagation()}
                    sx={{ [`& .${typographyClasses.root}`]: { fontWeight: 'bold' } }}
                  >
                    <FormControlLabel
                      value={normalizedDefaultModelUrl}
                      control={<Radio checked={isDefaultModelSelected} />}
                      label={`Default Model: ${systemModelType.displayName} - ${defaultModelId}`}
                    />
                  </RadioGroup>
                </FormControl>
              }
              subheader={defaultModelDetails?.meta.description}
            />
            <CardContent>
              {isDefaultModelLoading ? (
                <Grid item>
                  <Typography variant="body2">
                    <CircularProgress size={12} sx={{ mr: 1 }} />
                    Loading default model details...
                  </Typography>
                </Grid>
              ) : (
                Boolean(defaultModelDetails) && (
                  <Grid container direction="row" spacing={1}>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Date:
                      </Typography>
                      <CopyableText variant="body2">
                        {` ${moment(defaultModelDetails?.date).format('YYYY-MM-DD HH:mm:ss')}`}
                      </CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Model Type:
                      </Typography>
                      <CopyableText variant="body2">{` ${
                        modelTypesByApiModelValue[defaultModelDetails?.meta.modelType]?.text ??
                        (defaultModelDetails?.meta.modelType || 'N/A')
                      }`}</CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Project Name:
                      </Typography>
                      <CopyableText variant="body2">{` ${
                        defaultModelDetails?.meta.projectName || 'N/A'
                      }`}</CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Study ID:
                      </Typography>
                      <CopyableText variant="body2">{` ${defaultModelDetails?.meta.studyId || 'N/A'}`}</CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Stain Types:
                      </Typography>
                      <CopyableText variant="body2">
                        {` ${join(
                          map(defaultModelDetails?.meta.stainTypes, (stainTypeId) =>
                            stainTypeIdToDisplayName(stainTypeId)
                          ),
                          ', '
                        )}`}
                      </CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        End-to-End:
                      </Typography>
                      <CopyableText variant="body2">{` ${
                        defaultModelDetails?.meta?.end2end ? 'Yes' : 'No'
                      }`}</CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Training Type:
                      </Typography>
                      <CopyableText variant="body2">{` ${
                        defaultModelDetails?.meta.trainingType || 'N/A'
                      }`}</CopyableText>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                        Cancer Types:
                      </Typography>
                      <CopyableText variant="body2">
                        {` ${join(defaultModelDetails?.meta.tissueTypes, ', ') || 'N/A'}`}
                      </CopyableText>
                    </Grid>
                  </Grid>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
      <Grid item width="100%">
        <ModelsTable {...props} selectedModelUrl={resolvedSelectedModelUrl || ''} defaultModelUrl={defaultModelUrl} />
      </Grid>
    </>
  );
};
